import type { ApiError } from '@nx-paeonia/utils-http';
import { shopifyGet, shopifyPost, shopifyPostFormData } from '@nx-paeonia/utils-http';
import { andThen, err, ok, type Result } from '@nx-paeonia/utils-result';
import { type } from 'arktype';

export enum CartSections {
  cartOverlay = 'cart-overlay',
}

const CartItem = type({
  id: 'string',
  product_id: 'string',
  variant_id: 'string',
  quantity: 'number',
  title: 'string',
  price: 'number',
  line_price: 'number',
  handle: 'string',
  'image?': 'string',
  'variant_title?': 'string',
  vendor: 'string',
  properties: 'Record<string,string>',
  sku: 'string',
});

const Cart = type({
  attributes: 'Record<string,string>',
  items: CartItem.array(),
  item_count: 'number',
  total_price: 'number',
  total_weight: 'number',
  currency: 'string',
  requires_shipping: 'boolean',
  'note?': 'string',
  'sections?': {
    [CartSections.cartOverlay]: 'string',
  },
});

const AddToCartData = type({
  id: 'number | string',
  'quantity?': 'number',
  'properties?': 'Record<string,string>',
});
const SectionsResponse = type.Record('string', 'string');

export type CartItem = typeof CartItem.infer;
export type Cart = typeof Cart.infer;
export type AddToCartData = typeof AddToCartData.infer;
export type Sections = typeof SectionsResponse.infer;

// Tuple types to represent 1..5 section ids at the type level.
export type SectionsTuple =
  | [string]
  | [string, string]
  | [string, string, string]
  | [string, string, string, string]
  | [string, string, string, string, string];

// Accept either the tuple (compile-time enforced 1..5) or a string (CSV) for flexibility.
export type SectionsParam = SectionsTuple | string;

export type UpdateCartData = {
  id: string | number; // line item ID or variant ID
  quantity: number;
  properties?: Record<string, string>;
  sections?: SectionsParam;
};

export type CartError = ApiError | { type: 'InvalidVariant'; variantId: string | number };

export const getCart = (): Promise<Result<Cart, CartError>> => {
  return shopifyGet<Cart>('/cart.js');
};

export const addToCart = async (item: AddToCartData): Promise<Result<Cart, CartError>> => {
  const payload = {
    id: item.id,
    quantity: item.quantity || 1,
    properties: item.properties || {},
  };

  const result = await shopifyPost<{ items: CartItem[] }>('/cart/add.js', payload);

  return andThen(result, async () => {
    return await getCart();
  });
};

export const addAttributesToCart = async (attributes: Record<string, string>): Promise<Result<Cart, CartError>> => {
  const payload = {
    attributes: attributes,
  };

  return shopifyPost<Cart>('/cart/update.js', payload);
};

export const addFormDataToCart = async (fd: FormData): Promise<Result<string, CartError>> => {
  if (!fd.has('sections')) {
    fd.append('sections', CartSections.cartOverlay);
  }

  type Response = { items: CartItem[]; sections?: Sections };

  const result = await shopifyPostFormData<Response>('/cart/add.js', fd);

  return andThen(result, (result: Response) => {
    if (result.sections && result.sections[CartSections.cartOverlay]) {
      return ok(result.sections[CartSections.cartOverlay]);
    }

    return err<CartError>({ type: 'ParseError', message: `Missing section: '${CartSections.cartOverlay}' in response` });
  });
};

export const addMultipleToCart = async (items: AddToCartData[], attributes?: Record<string, string>): Promise<Result<Cart, CartError>> => {
  const payload: { items: AddToCartData[]; attributes?: Record<string, string>; note?: string } = {
    items: items.map((item) => ({
      id: item.id,
      quantity: item.quantity || 1,
      properties: item.properties || {},
    })),
  };

  if (attributes) {
    payload.attributes = attributes;

    if (Object.hasOwn(attributes, 'note')) {
      payload.note = attributes.note;
    }
  }

  const result = await shopifyPost<{ items: CartItem[] }>('/cart/add.js', payload);

  return andThen(result, async () => {
    return await getCart();
  });
};

export const updateCartItem = async (data: UpdateCartData): Promise<Result<Cart, CartError>> => {
  // Convert sections array to comma-separated string expected by Shopify
  let sectionsPayload: string | undefined;

  if (data.sections) {
    let sectionsArr = Array.isArray(data.sections)
      ? data.sections.map((s) => String(s).trim()).filter(Boolean)
      : String(data.sections)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

    if (sectionsArr.length > 5) {
      sectionsArr = sectionsArr.slice(0, 5); // Limit to 5 sections
    }

    if (sectionsArr.length > 0) {
      sectionsPayload = sectionsArr.join(',');
    }
  }

  const payload: UpdateCartData = {
    id: data.id,
    quantity: data.quantity,
    properties: data.properties,
  };

  if (sectionsPayload) {
    payload.sections = sectionsPayload;
  }

  return await shopifyPost<Cart>('/cart/change.js', payload);
};

// FIXME: This has to be done using updates.js
export const removeFromCart = async (itemId: string | number): Promise<Result<Cart, CartError>> => {
  return updateCartItem({ id: itemId, quantity: 0, sections: ['cart-overlay'] });
};

export const clearCart = (): Promise<Result<Cart, CartError>> => {
  return shopifyPost<Cart>('/cart/clear.js', {});
};

// Get cart item count
export const getCartItemCount = async (): Promise<Result<number, CartError>> => {
  const cartResult = await getCart();
  if (cartResult.success) {
    return ok(cartResult.data.item_count);
  }
  return cartResult;
};

// Get cart total price
export const getCartTotal = async (): Promise<Result<number, CartError>> => {
  const cartResult = await getCart();
  if (cartResult.success) {
    return ok(cartResult.data.total_price);
  }
  return cartResult;
};
